import {
  carouselResolveDataset,
  resolvePendingSlide
} from '@/shared/utils/carousel-resolve';
import { loadSlider } from '@/shared/utils/swiper';

type SwiperElement = HTMLElement & { swiper?: { update: () => void } };

const MAX_RESOLVE_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const resolveSlide = async (slideEl: HTMLElement) => {
  let resolved = false;
  // resolvePendingSlide() returning false is a confirmed result (the
  // product/collection genuinely has no image) — remove immediately, no
  // retry needed. A *thrown* error (network blip, gateway token fetch
  // failure, transient GraphQL error) is not a confirmed result, so retry
  // a bounded number of times before giving up — otherwise a single
  // transient failure permanently drops a perfectly valid carousel item,
  // reintroducing the missing-item bug this fallback exists to fix.
  for (let attempt = 1; attempt <= MAX_RESOLVE_ATTEMPTS; attempt += 1) {
    try {
      resolved = await resolvePendingSlide(slideEl);
      break;
    } catch (error) {
      if (attempt >= MAX_RESOLVE_ATTEMPTS) {
        console.error(error);
        break;
      }
      await wait(RETRY_DELAY_MS * attempt);
    }
  }
  slideEl.classList.remove(carouselResolveDataset.PENDING_CLASS);
  if (resolved) return;

  const root = slideEl.closest<HTMLElement>(
    '[data-section-name="category-carousel"]'
  );
  slideEl.remove();
  root?.querySelector<SwiperElement>('.js-category-carousel')?.swiper?.update();
};

// Pending slides count toward items_count optimistically in Liquid (the
// server can't tell "cap overflow" from "genuinely dead link" ahead of
// time), so a section whose items are all (or all-but-one) genuinely invalid
// still renders its shell (header/nav arrows) up front, matching Liquid's
// items_count > 0 / items_count > 1 gates. Once every pending slide in this
// root has settled, reconcile the shell with the final, real slide count.
const reconcileRootAfterSettle = (root: HTMLElement) => {
  if (window.Shopify?.designMode) return;
  const remaining = root.querySelectorAll('.swiper-slide').length;
  if (remaining === 0) {
    root.style.display = 'none';
    return;
  }
  if (remaining === 1) {
    root
      .querySelector<HTMLElement>('.slider-prev')
      ?.style.setProperty('display', 'none');
    root
      .querySelector<HTMLElement>('.slider-next')
      ?.style.setProperty('display', 'none');
  }
};

// Observing each pending slide individually would leave `reconcileRootAfterSettle`
// waiting forever for slides that never happen to scroll into view on their
// own (e.g. a carousel low enough on the page, or slides beyond what
// slidesPerView shows, that the user never reaches) — settlement would then
// never complete and the empty-shell/single-arrow cleanup would never run.
// Observing the whole root instead means "the carousel is near the
// viewport" resolves every one of its pending slides together, so
// settlement is always guaranteed to finish once the carousel is genuinely
// seen, not gated on each individual slide's own visibility.
const setupPendingSlideResolution = (root: HTMLElement) => {
  const pendingSlides = root.querySelectorAll<HTMLElement>(
    `.${carouselResolveDataset.PENDING_CLASS}`
  );
  if (!pendingSlides.length) return;

  const settleAll = () => {
    Promise.allSettled(
      Array.from(pendingSlides).map((slide) => resolveSlide(slide))
    ).then(() => reconcileRootAfterSettle(root));
  };

  try {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        obs.disconnect();
        settleAll();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    observer.observe(root);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    // Some old browsers are incompatible with IntersectionObserver.
    settleAll();
  }
};

export const setupCategoryCarousels = (scope: ParentNode = document) => {
  const roots = scope.querySelectorAll<HTMLElement>(
    '[data-section-name="category-carousel"]'
  );
  roots.forEach((root) => {
    if (root.dataset.carouselInitialized === 'true') return;
    root.dataset.carouselInitialized = 'true';

    const slider = root.querySelector<HTMLElement>('.js-category-carousel');
    if (slider) {
      const desktopAttr = slider.dataset.slidesDesktop;
      const mobileAttr = slider.dataset.slidesPerView;
      const desktopParsed = Number.parseFloat(desktopAttr || '5');
      const mobileParsed = Number.parseFloat(mobileAttr || '2');
      // floor() ensures repeated setup always produces base + 0.5 (peek),
      // not compounding increments if the attribute already holds a fractional value.
      const mobilePerView =
        Number.isFinite(mobileParsed) && mobileParsed > 0
          ? Math.floor(mobileParsed) + 0.5
          : 2.5;
      const desktopParsed5 = Number.isFinite(desktopParsed) ? desktopParsed : 5;
      const desktopPerView = Math.min(5, desktopParsed5);
      const tabletPerView = Math.max(2, desktopPerView - 2);

      slider.dataset.slidesPerView = String(mobilePerView);

      const prevEl = root.querySelector<HTMLElement>('.slider-prev');
      const nextEl = root.querySelector<HTMLElement>('.slider-next');
      loadSlider(slider, {
        navigation: prevEl && nextEl ? { prevEl, nextEl } : false,
        breakpoints: {
          768: { slidesPerView: tabletPerView, spaceBetween: 16 },
          1024: { slidesPerView: desktopPerView, spaceBetween: 16 }
        },
        pagination: false
      });
    }

    setupPendingSlideResolution(root);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setupCategoryCarousels();
  });
} else {
  setupCategoryCarousels();
}

document.addEventListener('shopify:section:load', (event) => {
  const target = (event as CustomEvent).target as HTMLElement | null;
  if (!target) return;
  if (!target.querySelector('[data-section-name="category-carousel"]')) return;

  const sliders = target.querySelectorAll<
    HTMLElement & { swiper?: { destroy: (a: boolean, b: boolean) => void } }
  >('.js-category-carousel');
  sliders.forEach((el) => el.swiper?.destroy(true, true));

  target
    .querySelectorAll<HTMLElement>('[data-section-name="category-carousel"]')
    .forEach((root) => {
      delete root.dataset.carouselInitialized;
    });
  setupCategoryCarousels(target);
});
